"use client";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";



// Define types for our gallery items and categories
type GalleryCategory = "residential" | "commercial" | "industrial";

interface GalleryImage {
  src: string;
  category: GalleryCategory;
}

export default function Gallery() {
  const searchParams = useSearchParams();
  const type = searchParams.get("t");

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });

  const popupSize = 320; // px size of the preview box
  const offset = 40;     // gap from cursor

  useEffect(() => {
    if (hoveredIndex === null) return;

    const { innerWidth, innerHeight } = window;
    let top = cursorPos.y + offset;
    let left = cursorPos.x + offset;

    // Check if overflowing right
    if (left + popupSize > innerWidth) {
      left = cursorPos.x - popupSize - offset;
    }

    // Check if overflowing bottom
    if (top + popupSize > innerHeight) {
      top = cursorPos.y - popupSize - offset;
    }

    // Clamp so it never goes off screen entirely
    top = Math.max(0, Math.min(top, innerHeight - popupSize));
    left = Math.max(0, Math.min(left, innerWidth - popupSize));

    setPopupPos({ top, left });
  }, [cursorPos, hoveredIndex]);

  // Gallery data
  const images: GalleryImage[] = [
    // commercial
    { src: "/images/pf_low/commercial/Montreal01-180x150.jpg", category: "commercial" },
    { src: "/images/pf_low/commercial/Montreal09-180x150.jpg", category: "commercial" },
    { src: "/images/pf_low/commercial/Montreal15a-180x150.jpg", category: "commercial" },
    { src: "/images/pf_low/commercial/Montreal16-180x150.jpg", category: "commercial" },

    // industrial
    { src: "/images/pf_low/industrial/Montreal05-180x150.png", category: "industrial" },
    { src: "/images/pf_low/industrial/Montreal06-180x150.jpg", category: "industrial" },
    { src: "/images/pf_low/industrial/Montreal07-e1360079877171-180x150.jpg", category: "industrial" },
    { src: "/images/pf_low/industrial/Montreal08-180x150.jpg", category: "industrial" },
    { src: "/images/pf_low/industrial/Montreal10-180x150.jpg", category: "industrial" },
    { src: "/images/pf_low/industrial/Montreal12-180x150.jpg", category: "industrial" },
    { src: "/images/pf_low/industrial/Montreal14-e1360079385882-180x142.jpg", category: "industrial" },

    // residential
    { src: "/images/pf_low/residential/340McL-180x150.jpg", category: "residential" },
    { src: "/images/pf_low/residential/Bishop14-180x150.jpeg", category: "residential" },
    { src: "/images/pf_low/residential/Bishop15-180x150.jpeg", category: "residential" },
    { src: "/images/pf_low/residential/Bishop16-180x150.jpeg", category: "residential" },
    { src: "/images/pf_low/residential/Bishop17-180x150.jpeg", category: "residential" },
    { src: "/images/pf_low/residential/Montreal02-180x150.jpg", category: "residential" },
    { src: "/images/pf_low/residential/Montreal03-180x150.jpg", category: "residential" },
    { src: "/images/pf_low/residential/Montreal04b-180x150.jpg", category: "residential" },
    { src: "/images/pf_low/residential/Montreal04c-180x150.jpg", category: "residential" },
    { src: "/images/pf_low/residential/Outremont01-180x150.jpg", category: "residential" },
    { src: "/images/pf_low/residential/Westmount01-180x150.jpg", category: "residential" },
    { src: "/images/pf_low/residential/Westmount02-180x150.jpg", category: "residential" },
    { src: "/images/pf_low/residential/Westmount03-180x150.jpg", category: "residential" },
    { src: "/images/pf_low/residential/Westmount4-180x150.jpg", category: "residential" },
    { src: "/images/pf_low/residential/Westmount05-180x150.jpg", category: "residential" },
    { src: "/images/pf_low/residential/Westmount06-180x150.jpg", category: "residential" },
    { src: "/images/pf_low/residential/Westmount07-180x150.jpg", category: "residential" },
    { src: "/images/pf_low/residential/Westmount08-180x150.jpg", category: "residential" },
  ];

  const imagesHigh: GalleryImage[] = [
    // commercial
    { src: "/images/pf_high/commercial/Montreal01.jpg", category: "commercial" },
    { src: "/images/pf_high/commercial/Montreal09.jpg", category: "commercial" },
    { src: "/images/pf_high/commercial/Montreal15a.jpg", category: "commercial" },
    { src: "/images/pf_high/commercial/Montreal16.jpg", category: "commercial" },

    // industrial
    { src: "/images/pf_high/industrial/Montreal05.png", category: "industrial" },
    { src: "/images/pf_high/industrial/Montreal06.jpg", category: "industrial" },
    { src: "/images/pf_high/industrial/Montreal07-e1360079877171.jpg", category: "industrial" },
    { src: "/images/pf_high/industrial/Montreal08.jpg", category: "industrial" },
    { src: "/images/pf_high/industrial/Montreal10.jpg", category: "industrial" },
    { src: "/images/pf_high/industrial/Montreal12.jpg", category: "industrial" },
    { src: "/images/pf_high/industrial/Montreal14-e1360079385882.jpg", category: "industrial" },

    // residential
    { src: "/images/pf_high/residential/340McL.jpg", category: "residential" },
    { src: "/images/pf_high/residential/Bishop14.jpeg", category: "residential" },
    { src: "/images/pf_high/residential/Bishop15.jpeg", category: "residential" },
    { src: "/images/pf_high/residential/Bishop16.jpeg", category: "residential" },
    { src: "/images/pf_high/residential/Bishop17.jpeg", category: "residential" },
    { src: "/images/pf_high/residential/Montreal02.jpg", category: "residential" },
    { src: "/images/pf_high/residential/Montreal03.jpg", category: "residential" },
    { src: "/images/pf_high/residential/Montreal04b.jpg", category: "residential" },
    { src: "/images/pf_high/residential/Montreal04c.jpg", category: "residential" },
    { src: "/images/pf_high/residential/Outremont01.jpg", category: "residential" },
    { src: "/images/pf_high/residential/Westmount01.jpg", category: "residential" },
    { src: "/images/pf_high/residential/Westmount02.jpg", category: "residential" },
    { src: "/images/pf_high/residential/Westmount03.jpg", category: "residential" },
    { src: "/images/pf_high/residential/Westmount4.jpg", category: "residential" },
    { src: "/images/pf_high/residential/Westmount05.jpg", category: "residential" },
    { src: "/images/pf_high/residential/Westmount06.jpg", category: "residential" },
    { src: "/images/pf_high/residential/Westmount07.jpg", category: "residential" },
    { src: "/images/pf_high/residential/Westmount08.jpg", category: "residential" },
  ];

  // State for active category
  const [activeCategory, setActiveCategory] =
    useState<GalleryCategory>((type ?? "residential") as GalleryCategory);

  // Filter images based on active category
  const filteredImages = images.filter(
    (img) => img.category === activeCategory,
  );
  const filteredHighImages = imagesHigh.filter(
    (img) => img.category === activeCategory,
  );

  return (
    <section
      id="gallery"
      className="w-full py-12 lg:py-[4.5rem] px-5 md:px-8 lg:px-0"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-8">
          <div className="mb-6 lg:mb-0">
            <span className="text-primary uppercase text-sm font-medium tracking-wider">
              Projects Gallery
            </span>
            <h2 className="text-3xl font-bold mt-2 text-foreground">
              Completed Work
            </h2>
          </div>

          {/* Category Buttons */}
          <div className="flex flex-wrap gap-6">
            <button
              onClick={() => setActiveCategory("residential")}
              className={`relative pl-6 rounded-md transition-colors
 ${activeCategory === "residential"
                  ? "text-primary font-bold"
                  : "text-foreground/80 hover:text-primary"
                }`}
            >
              <span
                className={`absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full
 ${activeCategory === "residential" ? "bg-primary" : "bg-secondary"}`}
              />
              Residential
            </button>
            <button
              onClick={() => setActiveCategory("commercial")}
              className={`relative pl-6 rounded-md transition-colors
 ${activeCategory === "commercial"
                  ? "text-primary font-bold"
                  : "text-foreground/80 hover:text-primary"
                }`}
            >
              <span
                className={`absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full
 ${activeCategory === "commercial" ? "bg-primary" : "bg-secondary"}`}
              />
              Commercial
            </button>
            <button
              onClick={() => setActiveCategory("industrial")}
              className={`relative pl-6 rounded-md transition-colors
 ${activeCategory === "industrial"
                  ? "text-primary font-bold"
                  : "text-foreground/80 hover:text-primary"
                }`}
            >
              <span
                className={`absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full
 ${activeCategory === "industrial" ? "bg-primary" : "bg-secondary"}`}
              />
              Industrial
            </button>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="relative">
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7">
            {filteredImages.map((image, index) => (
              <div
                key={index}
                className="cursor-pointer w-full rounded-md transition-all duration-300 hover:opacity-80"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onMouseMove={(e) =>
                  setCursorPos({ x: e.clientX, y: e.clientY })
                }
              >
                <div className="m-2 border-2 shadow-lg relative aspect-square">
                  <a href={filteredHighImages[index].src} target="_blank">
                    <img
                      src={image.src}
                      className="rounded-md absolute inset-0 h-full w-full object-cover transition-transform duration-300"
                      loading="lazy"
                    />
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Popup Preview */}
          <div
            className={`fixed z-50 pointer-events-none border-2 border-white rounded-md shadow-xl bg-black 
    transition-all duration-300 ease-out
    ${hoveredIndex !== null ? "opacity-100 scale-100" : "hidden"}`}
            style={{
              top: popupPos.top,
              left: popupPos.left,
              width: popupSize,
              height: popupSize,
            }}
          >
            {hoveredIndex !== null && (
              <img
                src={filteredHighImages[hoveredIndex].src}
                className="w-full h-full object-cover rounded-md"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
